import { createRooms } from '@cascade/core/connection';
import type { TSocket } from '@cascade/core/connection';
import { RequestError } from '@cascade/core/errors';
import { useEffect, useMemo } from 'react';
import { useLatest } from '#hooks/use-latest/index.ts';
import type { TServerAction } from '#providers/conversations/Conversations.types.ts';
import type { TConversationSocketOptions } from './useConversationSocket.types.ts';

export const useConversationSocket = (
  socket: TSocket,
  {
    isEnabled,
    projectId,
    conversationId,
    onEvent,
    onRoomError,
    onReconnect,
  }: TConversationSocketOptions,
): void => {
  const onEventRef = useLatest(onEvent);
  const onRoomErrorRef = useLatest(onRoomError);
  const onReconnectRef = useLatest(onReconnect);

  const rooms = useMemo(
    () =>
      createRooms({
        join: (payload) => socket.send('conversation.join', { payload }),
        leave: (payload) => socket.send('conversation.leave', { payload }),
      }),
    [socket],
  );

  useEffect(() => {
    if (isEnabled) {
      socket.connect();

      return;
    }

    socket.disconnect();
  }, [socket, isEnabled]);

  useEffect(() => {
    if (!projectId || !conversationId) {
      return;
    }

    const room = rooms.join({
      project_id: projectId,
      conversation_id: conversationId,
    });

    room.ready.catch((error: unknown) => {
      if (error instanceof RequestError && error.detail.kind !== 'aborted') {
        onRoomErrorRef.current(error);
      }
    });

    return () => {
      room.leave();
    };
  }, [rooms, projectId, conversationId, onRoomErrorRef]);

  useEffect(
    () =>
      socket.onAny((event, envelope) => {
        onEventRef.current({ type: event, envelope } as TServerAction);
      }),
    [socket, onEventRef],
  );

  useEffect(() => {
    let hasDisconnected = false;

    return socket.onStatus((status) => {
      if (status.state === 'disconnected') {
        hasDisconnected = true;

        return;
      }

      if (status.state !== 'connected') {
        return;
      }

      rooms.rejoinAll();

      if (!hasDisconnected) {
        return;
      }

      hasDisconnected = false;

      if (!status.recovered) {
        onReconnectRef.current();
      }
    });
  }, [socket, rooms, onReconnectRef]);
};
